// src/pages/Reports/IssuanceReporting/AgentPrintHouseSummary/TicketSoldTab.tsx
// RPT-011 (README.md mục 7) — vé đã bán, SUY DIỄN = Đã giao − Thu hồi (hệ thống chưa có nguồn dữ
// liệu bán vé thật/POS tại đại lý — giới hạn đã xác nhận với người dùng khi thiết kế BE).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getTicketSoldReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import AgentSelect from '../shared/AgentSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, ReportPaginationState, TicketSoldReportRow } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const TicketSoldTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<TicketSoldReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);

  useEffect(() => { loadAgentsWithCache().then(setAgents); }, []);

  const buildFilterParams = useCallback(
    () => ({ IssuancePlanId: planId || undefined, AgentId: agentId || undefined, Symbol: symbol || undefined }),
    [planId, agentId, symbol],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTicketSoldReportApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_ve_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, agentId, symbol]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/Sold`, buildFilterParams(), format, 'VeDaBan');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Alert color="warning" className="py-2">
        <i className="ri-information-line align-bottom me-1"></i>
        {st('so_lieu_ve_da_ban_la_message')}
      </Alert>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={4}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={4}>
          <Label className="form-label">{st('dai_ly_label')}</Label>
          <AgentSelect value={agentId} onChange={(v) => { setAgentId(v); resetPage(); }} />
        </Col>
        <Col md={4}>
          <Label className="form-label">{st('ky_hieu_label')}</Label>
          <Input type="text" placeholder={st('vd_n_b_placeholder')} value={symbol} onChange={(e) => { setSymbol(e.target.value); resetPage(); }} />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('dai_ly_label')}</th>
                <th>{st('loai_ve_column')}</th>
                <th>{st('ky_hieu_label')}</th>
                <th className="text-end">{st('sl_ban_column')}</th>
                <th className="text-end">{st('gia_tri_ban_column')}</th>
                <th>{st('ngay_ban_gan_nhat_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.issuancePlanId}-${row.agentId}-${row.symbol}`}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.planCode}</td>
                  <td>
                    <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                    <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                  </td>
                  <td>{row.ticketTypeName || row.ticketType}</td>
                  <td>{row.symbol}</td>
                  <td className="text-end text-success">{formatQty(row.soldQuantity)}</td>
                  <td className="text-end">{formatCurrency(row.soldValue)}</td>
                  <td>{formatDateTime(row.lastDeliveredAt)}</td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default TicketSoldTab;
